// @flow
import * as React from 'react';
import {
  ClientFilter,
  DataGrid, DataGridColumn,
  FilterBadges,
  ListPageWrapper,
  PageTitle, SlasSubscription, TagsView,
  useSessionStorageState,
  useSlasSubscription
} from '../../shared';
import { FunnelSimple } from '@phosphor-icons/react';
import { Box, Button, Link as MuiLink, Typography } from '@mui/material';
import moment from 'moment';
import _ from 'lodash';
import { getUserName } from '@coverright/utils';
import { formatToPhoneNumber } from '@coverright/ui/inputs';
import { Link } from 'react-router-dom';
import { getRoute } from '../../app/router';
import {
  getDateRangeFromPeriod,
  Periodic,
  StatusView,
  TableSettings,
  useTableSettings,
  withTableSettings
} from '../../features';
import {
  AttemptedLeadFilterInput,
  PageInput,
  SlaFilterInput,
  StatusDto
} from '@coverright/data-access/types/enrollment';
import { useTwoHoursSlaFilterDrawer, withTwoHoursSlaFilterDrawer } from './ui/two-hours-sla-filter-drawer';
import { useMemo } from 'react';

const initialFilter: Periodic<SlaFilterInput, 'range'> = {
  range: {
    period: 2
  }
}

const Page = ({nerdWallet}: {nerdWallet?: boolean}) => {
  const [filters, setFilters] = useFilters();
  const {columns} = useTableSettings(slaColumns, filters, settings => setFilters(settings.filters || initialFilter));
  const [page, setPage] = usePage();

  const filterInput = useMemo(() => ({
    ...filters,
    range: getDateRangeFromPeriod(filters.range),
    justNerdWallet: nerdWallet
  }), [filters]);

  const {loading, data} = useSlasSubscription({
    variables: {
      filter: filterInput,
      pageInput: page
    },
  })
  const {open} = useTwoHoursSlaFilterDrawer();

  const openFilters = () => open(setFilters, filters);

  const onFilterRemoveClick = (filterName: keyof AttemptedLeadFilterInput) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  };


  return (
    <ListPageWrapper>
      <PageTitle title="2 Hour SLA">
        <TableSettings />

        <Button variant={'outlined'}
                onClick={openFilters}
                startIcon={<FunnelSimple weight={'bold'} />}
                size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
          Filters
        </Button>
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filters} nameMapFunc={filterNameFunc} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={data?.slas.totalElements}
                loading={loading}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={data?.slas.data || []}/>
    </ListPageWrapper>
  );
};

export const TwoHourSla = withTwoHoursSlaFilterDrawer(withTableSettings(Page, 'sla'));
export const TwoHourSlaNW = withTwoHoursSlaFilterDrawer(withTableSettings(() => <Page nerdWallet />, 'nwSla'));

const slaColumns: DataGridColumn<SlasSubscription['slas']['data'][0], ''>[] = [
  {
    title: 'Name',
    value: v => <MuiLink component={Link}
                         color={'text.primary'}
                         sx={{textDecoration: 'none'}}
                         onClick={(event: any) => event.stopPropagation()}
                         to={`/${getRoute('client')}/${v.clientId}`}>
      {getUserName(v)}
    </MuiLink>,
    sortKey: ''
  },
  {
    title: 'Phone',
    value: v => formatToPhoneNumber(v.phoneNumber)
  },
  {
    title: 'Quote submitted time',
    value: v => moment(v.createdAt).format('L H:mm:ss'),
  },
  {
    title: 'Status',
    value: v => <StatusView status={v.status} />
  },
  {
    title: 'State',
    value: v => v.state
  },
  {
    title: 'Tags',
    value: v => (
      <Box sx={{'& > div': {flexWrap: 'initial'} }}>
        <TagsView tags={v.tags} />
      </Box>
    )
  },
  {
    title: 'Calls',
    value: v => `${v.successfulCallsCount}/${v.callsCount}`
  },
  {
    title: 'First Call Time',
    value: v => [
      v.firstCallAt ? moment(v.firstCallAt).format('HH:mm') : '-',
      v.firstCallAgentName ? `(${v.firstCallAgentName})` : ''
    ].join(' ')
  },
  {
    title: 'Called within 5 min',
    value: v => {
      if (!v.firstCallAt) return (
        <Typography variant={'body2'} color={'error'} fontWeight={500}>No</Typography>
      );
      const diff = moment(v.firstCallAt).diff(v.createdAt) / 1000 / 60;
      return <Typography variant={'body2'} color={diff > 5 ? 'error' : 'success.500'} fontWeight={500}>{diff > 5 ? 'No' : 'Yes'}</Typography>
    }
  },
  {
    title: 'Expected 2nd Call',
    value: v => {
      if (!v.firstCallAt) return '-';
      if (v.status === StatusDto.Attempted) {
        return moment(v.firstCallAt).add(2, 'hour').format('HH:mm')
      }
      return 'N/A'
    }
  },
  {
    title: '2nd Call Time',
    value: v => {
      if (!v.firstCallAt) return 'N/A';
      if (!v.secondCallAt) return '-';
      return [
        v.secondCallAt ? moment(v.secondCallAt).format('L H:mm') : '-',
        v.secondCallAgentName ? `(${v.secondCallAgentName})` : ''
      ].join(' ')
    }
  },
  {
    title: 'Called Within 2 hrs',
    value: v => {
      if (!v.firstCallAt) return '-';
      if (!v.secondCallAt) return 'N/A';
      const value = moment(v.secondCallAt).isSameOrBefore(moment(v.firstCallAt).add(2, 'hour'));
      return  (
        <Typography variant={'body2'} color={value ? 'success.500' : 'error'} fontWeight={500}>{value ? 'Yes' : 'No'}</Typography>
      )
    }
  },
]

const filterNameFunc = (name: keyof ClientFilter) => {
  switch (name) {
    case 'range': return 'Created';
    default: return name;
  }
}

const usePage = () => {
  return useSessionStorageState<PageInput>(
    'slaPage',
    { page: 0, size: 20 }
  );
};

const useFilters = () => {
  return useSessionStorageState<Periodic<SlaFilterInput, 'range'>>(
    'slaFilters',
    initialFilter
  );
};
